import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AccountViewModel } from '@models/account.class';
import { BankAccountViewModel } from '@models/bank-account.class';
import { IContract, LegalPersonalityType } from '@models/contract.class';
import { EventError } from '@models/tracking-error-event.class';
import { EventSuccess } from '@models/tracking-success-event.class';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import { isFinite } from 'lodash';
import {
  BehaviorSubject,
  EMPTY,
  empty as observableEmpty,
  forkJoin,
  Observable,
  of as observableOf,
  Subscription,
  throwError as observableThrowError,
} from 'rxjs';
import {
  catchError,
  distinctUntilKeyChanged,
  filter,
  first,
  map,
  pluck,
  switchMap,
  take,
} from 'rxjs/operators';
import {
  appRoutes,
  AppState,
  HTTP_STATUS_CODE,
  LEGAL_PERSONALITY_TYPE,
  OPERATION_MODAL_OPENED,
  TRACKING_EVENTS,
} from 'src/app';
import { featureFlags } from 'src/app/featureFlags';
import { MFA_TYPE, TOKEN_INFO_TYPE } from 'src/core/models/mfa.class';
import {
  NewRelicLogger,
  NEW_RELIC_KNOWN_ERRORS,
} from 'src/components/shared/newrelicLogger/newRelicLogger.class';
import { SideModalComponent } from 'src/components/side-modal/side-modal.component';
import {
  CashService,
  ContractsService,
  NotificationService,
  OperationService,
  PatronService,
} from 'src/core';
import { CashLayers } from 'src/core/models/withdrawal.class';
import { MixpanelService } from 'src/core/services/mixpanel.service';

@Component({
  selector: 'hb-trade-cash',
  templateUrl: './tradeCash.component.html',
})
export class TradeCashComponent implements OnInit, OnDestroy {
  @ViewChild('sideModal') sideModal: SideModalComponent;
  @Input() operationStatus: boolean;
  @Input() isUsaContract: boolean;
  @Input() selectedLoremAccount: AccountViewModel | IContract;
  @Input() isGeolocationEnabled: boolean;
  @Input() getContractCashLayers$: Observable<CashLayers>;

  @Output() changeOperationStatus = new EventEmitter<boolean>();
  @Output() onCloseWithdrawal: EventEmitter<boolean> = new EventEmitter();

  public scrolledSet = false;
  public scrollableHeight: any = 'initial';
  public showMessageContractStatus = false;
  public hasBankAccount = true;
  public currentContractID: any;
  public originAccountName = '';
  public originAccountLabel = 'Contrato origen';
  public isOperative = true;
  public accountList: any = [];
  public cashList: any = [];
  public onlyOneAccount = false;
  public defaultAccount: any;
  public withdrawal = 0;
  public order: any = {};
  public needsToken = true;
  public showConfirmation = false;
  public lockConfirmation = false;
  public lockSecondConfirmation = false;
  public token: string;
  public resetPrice = 0;
  public clearCombo = false;
  public tokenIsInvalid = false;
  public tokenSPEI = '';
  public showSPEIConfirmation = false;
  public canOperate = true;
  public lockCancel = false;
  public challengeMessageType: number;
  public mfaType: number;
  public isDiscretional: boolean;
  public isBalanceVisible = false;
  public isServiceInterrupted = false;
  public showPermanenceMessageV2: boolean;
  public withdrawalAmount = 0;

  private elPatronData: any = {};
  private MAX_AMOUNT = 0; // always operative
  private selectedAccount: {
    id: number;
    isValid: boolean;
  };
  private concepto = '';
  private referencia = '';
  private readToken: any;
  private userHasBankAccountIssues: boolean;
  private userHasPartyId: any;
  private isLoremAccount = false;
  private currentContract: IContract;
  private bankList: any = [];
  private bankWithIcon: string[] = [
    '002',
    '012',
    '014',
    '019',
    '021',
    '030',
    '036',
    '044',
    '058',
    '072',
    '127',
    '131',
    '134',
    '137',
    '143',
    '147',
  ];
  private userCanUseAccountManager: boolean;
  private subscriptions: Subscription = new Subscription();
  private updateContractLayers$: BehaviorSubject<void> = new BehaviorSubject(
    null,
  );
  constructor(
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private el: ElementRef,
    private cashService: CashService,
    private operationService: OperationService,
    private patronService: PatronService,
    private newRelicLogger: NewRelicLogger,
    private router: Router,
    private mixpanelService: MixpanelService,
    private contractsService: ContractsService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit() {
    const successEvent = new EventSuccess();
    successEvent.setType(this.isUsaContract);
    const successEventData = successEvent.getEventSuccess();

    this.mixpanelService.track(
      TRACKING_EVENTS.view_withdrawal_form.event,
      successEventData,
    );

    this.getAccountLabelAndName();
    this.lockCancel = false;

    this.subscriptions.add(
      this.store
        .select('loggedInUser')
        .pipe(map((values: any) => values.toJS()))
        .subscribe((value: any) => {
          this.readToken = value.sessionToken;
          this.mfaType =
            value.mfaType === TOKEN_INFO_TYPE.SMS
              ? MFA_TYPE.SMS
              : MFA_TYPE.TOKEN;
          this.mfaType =
            value.mfaType === TOKEN_INFO_TYPE.DISABLED
              ? MFA_TYPE.DISABLED
              : this.mfaType;
        }),
    );

    this.subscriptions.add(
      this.store
        .select('currentContract')
        .pipe(
          map((currentContract: any) => currentContract.toJS()),
          filter(
            (currentContract) => currentContract && currentContract.contractID,
          ),
          distinctUntilKeyChanged('contractID'),
        )
        .subscribe((currentContract: IContract) => {
          const { isDiscretional, contractID, isLoremAccount, active } =
            currentContract;
          this.currentContract = currentContract;
          this.getDiscretionalStatus(isDiscretional);

          this.reset(true);
          this.currentContractID = contractID;
          this.isLoremAccount = isLoremAccount;
          this.scanForUserBankAccounts();
          this.withdrawalAmount = 0;
          this.getBankList();
          this.getBankAccounts();
          this.subscribeToContractLayers();

          this.showMessageContractStatus = !active;
        }),
    );

    this.subscriptions.add(
      this.store
        .select('elPatronData')
        .pipe(
          map((elPatronData: any) => elPatronData.toJS()),
          filter(
            (elPatronData: any) => elPatronData.URL_CHALLENGE !== undefined,
          ),
          take(1),
        )
        .subscribe((elPatronData: any) => {
          this.elPatronData = elPatronData;
        }),
    );

    this.scanForUserPartyId();
    this.scanForIsServiceInterrupted();

    this.showPermanenceMessageV2 = featureFlags.tmpPermanenceRule.isEnabled();
  }

  ngOnDestroy() {
    this.sideModal.handleHide();
    this.subscriptions.unsubscribe();
  }

  reset(emptyBanks: boolean = false) {
    const parent = this.el.nativeElement;

    this.withdrawal = 0;
    this.showConfirmation = false;
    this.resetPrice = 0;
    this.onlyOneAccount = false;
    this.selectedAccount = undefined;
    this.lockConfirmation = false;
    this.lockSecondConfirmation = false;
    this.token = '';
    this.showSPEIConfirmation = false;
    this.tokenSPEI = '';
    this.hasBankAccount = true;
    this.concepto = '';
    this.referencia = '';
    this.lockCancel = false;
    this.needsToken = true;
    if (emptyBanks) {
      this.accountList = [];
      this.cashList = [];
    }

    if (parent.querySelector('.concepto') !== null) {
      parent.querySelector('.concepto').value = '';
    }

    if (parent.querySelector('.referencia') !== null) {
      parent.querySelector('.referencia').value = '';
    }

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  changeToken(event: any) {
    if (event.length > 6) {
      return false;
    } else if (event.length === 6) {
      return true;
    } else {
      return false;
    }
  }

  changeSecondToken(event: any, lockFirstConfirmation: boolean = false) {
    if (!this.changeToken(event)) {
      if (lockFirstConfirmation) {
        this.lockConfirmation = true;
      }
      this.lockSecondConfirmation = true;
    } else {
      if (lockFirstConfirmation) {
        this.lockConfirmation = false;
      }
      this.tokenSPEI = event;
      this.lockSecondConfirmation = false;
    }
  }

  cleanNumber(value: any, replace: boolean = true): number {
    if (!replace) {
      return value;
    }

    if (value === undefined) {
      return 0.0;
    }
    if (value.toString().indexOf(',') !== -1) {
      value = value.toString().replace(/\,/g, '');
    } else {
      value = value.toString();
    }
    return parseFloat(value.trim().replace(/\$/g, '').replace(/MXN/g, ''));
  }

  setPrice(event: any) {
    if (typeof event === 'string') {
      event = this.cleanNumber(event, true);
    }
    this.withdrawal = isFinite(event) && event > -1 ? event : 0;
    this.resetPrice = this.withdrawal;
    if (this.withdrawal > this.MAX_AMOUNT) {
      this.needsToken = true;
    } else {
      this.needsToken = false;
    }

    this.isValidWithdrawalAmount();
    this.isWithDrawalReady(true);
  }

  setWithdrawalAccount(event: any) {
    this.selectedAccount = { ...event };
    this.isValidWithdrawalAccount();
    this.isWithDrawalReady(true);
  }

  parseBankFullName(bankInfo: BankAccountViewModel) {
    let auxBankName: string = bankInfo.bankName;

    if (bankInfo.bankName.length > 24) {
      auxBankName = bankInfo.bankName.substring(0, 24) + '...';
    }

    const bankInformation = this.getBankInfo(bankInfo.sobBankId);
    const icon =
      bankInformation.icon &&
      `<div class="combo-icon" *ngIf="onlyOneAccount"><img class="icon" src="${bankInformation.icon}"></div>`;

    return `${icon}<span>${auxBankName}</span><span>-</span><span> ${bankInfo.bankAccountNumber.substring(
      bankInfo.bankAccountNumber.length - 4,
      bankInfo.bankAccountNumber.length,
    )}</span>`;
  }

  getBankAccounts() {
    const WAIT_TIME = 50;
    if (this.isLoremAccount) {
      if (
        this.userHasBankAccountIssues === undefined ||
        this.userHasPartyId === undefined
      ) {
        setTimeout(() => {
          this.getBankAccounts();
        }, WAIT_TIME);
      } else {
        this._getBankAccounts();
      }
    } else {
      this._getBankAccounts();
    }
  }

  _getBankAccounts() {
    if (
      featureFlags.isAccountManagerEnableToLegacy.isEnabled() &&
      this.userCanUseAccountManager
    ) {
      if (
        !this.userHasBankAccountIssues &&
        this.currentContract.legalPersonalityType ===
          LEGAL_PERSONALITY_TYPE.PHYSICAL_PERSON
      ) {
        return this.showMissingBankAccount();
      }
    } else if (this.isLoremAccount && !this.userHasBankAccountIssues) {
      return this.showMissingBankAccount();
    }

    this.subscriptions.add(
      this.getBankAccountsEndpoint().subscribe(
        (bankAccounts) => {
          if (!bankAccounts.length) {
            this.showMissingBankAccount();
          } else {
            this.hasBankAccount = true;
            this.sideModal.showModal();

            this.accountList = bankAccounts.map(
              (bankAccount, index: number) => {
                return {
                  id: index + 1,
                  bankName: bankAccount.bankName,
                  name: this.parseBankFullName(bankAccount),
                  bankId: bankAccount.sobBankId,
                  userBank: bankAccount.bankAccountNumber,
                  accountId: bankAccount.sobAccountId,
                  isValid: bankAccount.isValidAccount,
                };
              },
            );

            if (this.accountList.length < 2 && !this.onlyOneAccount) {
              this.onlyOneAccount = true;
              this.defaultAccount = this.accountList[0];
              this.setWithdrawalAccount(this.accountList[0]);
            } else {
              this.onlyOneAccount = false;
              this.defaultAccount = undefined;
            }
          }
        },
        (error) => {
          if (error._status === HTTP_STATUS_CODE.FAILED_OR_CANCELED) {
            this.sendOperativeMessage(
              '',
              'No se pudo obtener la cuenta bancaria, favor de intentarlo nuevamente.',
            );
          }
        },
      ),
    );
  }

  isValidWithdrawalAmount() {
    if (this.withdrawal <= 0 || isNaN(this.withdrawal)) {
      return false;
    } else {
      return true;
    }
  }

  isValidWithdrawalAccount(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    const isValid = this.selectedAccount && this.selectedAccount.id > 0;

    if (!onlyCheck) {
      const selectInput = parent.querySelector('.select input');
      isValid
        ? selectInput && selectInput.classList.remove('invalid')
        : selectInput && selectInput.classList.add('invalid');
    }

    return isValid;
  }

  isValidReference(onlyCheck: boolean = false) {
    return this.referencia.length >= 0 && this.referencia.length <= 7;
  }

  isWithDrawalReady(onlyCheck: boolean = false) {
    return (
      this.isValidWithdrawalAccount(onlyCheck) ||
      this.isValidWithdrawalAmount() ||
      this.isValidReference()
    );
  }

  get isFormValid() {
    return !!(
      this.isValidWithdrawalAccount(true) &&
      this.withdrawalAmount >= this.withdrawal &&
      this.withdrawal > 0 &&
      this.currentContractID
    );
  }

  get invalidAmount() {
    return this.withdrawal > this.withdrawalAmount;
  }

  resetBankCombo() {
    this.clearCombo = true;

    setTimeout(() => {
      this.clearCombo = false;
    }, 200);
  }

  doWithdrawalAmount(item: any, includeToken: boolean = false) {
    const startTime: any = new Date();
    const data: any = {
      amount: this.withdrawal,
      bankId: item.bankId.toString(),
      checkingAccountId: item.accountId.toString(),
      contractId: this.currentContractID,
    };
    const hash: any = this.newRelicLogger.buildHash(
      startTime.getTime(),
      this.currentContractID.toString(),
      data.bankId + data.checkingAccountId,
      parseInt(this.withdrawal.toString(), 10),
      'RET',
    );

    if (includeToken) {
      data.code = this.tokenSPEI;
    }

    if (this.concepto.length > 0) {
      data.concept = this.concepto;
    }

    if (this.referencia.length > 0) {
      data.numericReference = this.referencia;
    }
    data.hash = hash;

    this.subscriptions.add(
      this.operationService
        .registerWithDrawal(data)
        .pipe(
          catchError((error: any) => {
            if (!error.ErrorMessage) {
              error.ErrorMessage =
                error.error === 'invalid-mfa-code'
                  ? 'El código no es válido, intenta con otro por favor.'
                  : 'Ocurrió un error, favor de intentarlo nuevamente.';
            }

            const customAttributes = {
              _type: 'Cash',
            };

            this.newRelicLogger.setEvent(
              NEW_RELIC_KNOWN_ERRORS.tradeError,
              error,
              customAttributes,
            );

            this.notificationService.showServiceError({
              ...error,
              IsBussinessError: false,
            });

            this.cancelConfirmation();

            return EMPTY;
          }),
        )
        .subscribe(
          (value: any) => {
            this.lockCancel = false;
            this.reset();
            this.notificationService.showSuccess({
              title: '',
              message: `Solicitud de retiro aceptada`,
              eventId: undefined,
              isBusinessError: false,
            });

            this.resetBankCombo();
            this.updateContractLayers$.next();
            const isRealTime = true;
            this.contractsService.getPositionSummaryBatch(
              this.currentContractID,
              isRealTime,
            );
            this.onCloseWithdrawal.next(true);

            const successEvent = new EventSuccess();
            successEvent.setType(this.isUsaContract);
            const successEventData = successEvent.getEventSuccess();

            this.mixpanelService.track(TRACKING_EVENTS.withdrawal.event, {
              ...successEventData,
              amount: data.amount,
              source: successEvent.getType(),
            });
          },
          (error: { ErrorMessage: string }) => {
            const eventError = new EventError();
            eventError.setType(this.isUsaContract);
            eventError.setCallback(error.ErrorMessage);
            const eventErrorData = eventError.getEventError();

            this.mixpanelService.track(TRACKING_EVENTS.withdrawal.event, {
              ...eventErrorData,
              amount: data.amount,
              source: eventError.getType(),
            });
          },
        ),
    );
  }

  sendOperativeMessage(titleText: string, messageText: string) {
    let notificationMessage: any = {};

    notificationMessage = {
      message: messageText || 'Necesitas ingresar el token',
      title: titleText || 'Token Necesario',
    };
    this.notificationService.showWarning(notificationMessage);
  }

  doConfirmation() {
    if (this.selectedAccount) {
      this.showConfirmation = true;
      this.lockConfirmation = !this.isOperative;
      this.order = {
        ...this.selectedAccount,
      };

      if (this.isOperative) {
        this.lockConfirmation = this.isOperative && this.needsToken;
      }

      this.store.dispatch({
        type: OPERATION_MODAL_OPENED,
        payload: { isModalOpened: this.showConfirmation },
      });
    }
  }

  challengeDueMaxAmmount() {
    if (!this.needsToken) {
      this.doConfirmation();
    } else {
      this.subscriptions.add(
        this.patronService
          .getChallenge()
          .pipe(
            catchError((error: any) => {
              const formatedMessage =
                error.message ||
                'Ocurrió un error, favor de intentarlo nuevamente';
              this.sendOperativeMessage('Error', formatedMessage);
              return observableEmpty();
            }),
          )
          .subscribe((value: any) => {
            if (value.mfaPreference === 'Disabled') {
              this.mfaType = MFA_TYPE.DISABLED;
              this.challengeMessageType = MFA_TYPE.DISABLED;
            } else {
              this.mfaType =
                value.mfaPreference === 'SMS' ? MFA_TYPE.SMS : MFA_TYPE.TOKEN;
              this.challengeMessageType =
                value.mfaPreference === 'SMS' ? MFA_TYPE.SMS : MFA_TYPE.TOKEN;
            }
            this.doConfirmation();
          }),
      );
    }
  }

  tryWithdrawalAmount() {
    if (!this.canOperate) {
      return;
    }
    this.tokenIsInvalid = false;
    this.token = '';
    this.isWithDrawalReady() && this.challengeDueMaxAmmount();
  }

  setShowConfirmation(toShow: boolean = false) {
    this.showConfirmation = toShow;
    this.lockConfirmation = !this.isOperative;

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  cancelConfirmation() {
    this.showConfirmation = false;
    this.lockConfirmation = false;
    this.token = '';

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  cancelSPEIConfirmation() {
    this.lockConfirmation = true;
    this.lockSecondConfirmation = false;
    this.showSPEIConfirmation = false;
    this.token = '';
    this.tokenSPEI = '';

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showSPEIConfirmation },
    });
  }

  doPreWithdrawalAmount() {
    if (!this.isOperative) {
      if (this.token.length !== 6) {
        this.lockConfirmation = false;
        this.tokenIsInvalid = true;
      } else {
        this.tokenIsInvalid = false;
        this.lockCancel = true;
        this.subscriptions.add(
          this.patronService
            .upgradeToken(
              this.elPatronData.URL_UPGRADE_SESSION,
              this.readToken,
              this.token,
            )
            .pipe(
              catchError((error: any) => {
                this.sendOperativeMessage('Token incorrecto', error.message);

                this.lockConfirmation = false;
                this.lockCancel = false;
                return observableThrowError(error);
              }),
            )
            .subscribe((value: any) => {
              this.doWithdrawalAmount(this.order);
            }),
        );
      }
    } else {
      this.doWithdrawalAmount(this.order);
    }
  }

  doPreWithdrawalMaxAmount() {
    if (this.token.length !== 6) {
      this.lockConfirmation = false;
      this.tokenIsInvalid = true;
    } else {
      this.tokenIsInvalid = false;

      this.subscriptions.add(
        this.patronService
          .upgradeToken(
            this.elPatronData.URL_UPGRADE_SESSION,
            this.readToken,
            this.token,
          )
          .pipe(
            catchError((error: any) => {
              this.sendOperativeMessage('Token incorrecto', error.message);

              this.lockConfirmation = false;
              this.lockCancel = false;
              return observableThrowError(error);
            }),
          )
          .subscribe((value: any) => {
            this.showSPEIConfirmation = true;
            this.showConfirmation = false;
            this.lockSecondConfirmation = true;
            this.lockCancel = false;

            this.patronService
              .getChallenge()
              .pipe(
                catchError((error: any) => {
                  this.sendOperativeMessage('Error', error.message);

                  return observableOf(error);
                }),
              )
              .subscribe((valueShadowed: any) => {
                this.store.dispatch({
                  type: OPERATION_MODAL_OPENED,
                  payload: { isModalOpened: this.showSPEIConfirmation },
                });
              });
          }),
      );
    }
  }

  doPreWithdrawalSPEIConfirmation() {
    if (this.lockSecondConfirmation) {
      return;
    }
    if (this.tokenSPEI.length !== 6) {
      this.lockSecondConfirmation = false;
      this.lockConfirmation = false;
      this.tokenIsInvalid = true;
    } else {
      this.tokenIsInvalid = false;
      this.lockConfirmation = true;
      this.lockSecondConfirmation = true;
      this.lockCancel = true;
      this.doWithdrawalAmount(this.order, true);
    }
  }

  doMovement() {
    if (this.lockConfirmation) {
      return;
    }
    this.lockConfirmation = true;
    this.lockCancel = true;
    if (this.needsToken) {
      // AMMOUNT MORE THAN 8000
      if (this.isOperative) {
        this.doPreWithdrawalSPEIConfirmation();
      } else {
        this.doPreWithdrawalMaxAmount();
      }
    } else {
      this.doPreWithdrawalAmount();
    }
  }

  getDiscretionalStatus(discretionalParameter: any) {
    this.isDiscretional = discretionalParameter;
  }

  private subscribeToContractLayers() {
    // Subscribe to updateContractLayers$ so we can update by emitting from
    // that subject. And since it's a BehaviorSubject, it'll emit automatically,
    // triggering the request immediately when this code runs.
    this.subscriptions.add(
      this.updateContractLayers$
        .pipe(switchMap(() => this.getContractCashLayers$))
        .subscribe(({ availableToWithdraw, cashList }) => {
          this.withdrawalAmount = availableToWithdraw;
          this.cashList = cashList;
        }),
    );
  }

  doScrollArragenment() {
    const headerHeight = 373;
    const MIN_HEIGHT = 200;

    if (window.scrollY > 120) {
      if (this.scrolledSet) {
        return;
      }
      this.scrolledSet = true;

      this.scrollableHeight =
        window.innerHeight - headerHeight > MIN_HEIGHT
          ? `${window.innerHeight - headerHeight}px`
          : `${MIN_HEIGHT}px`;
    } else {
      this.scrolledSet = false;
      this.scrollableHeight = 'initial';
    }
  }

  get canDisplayWithdrawalButton(): boolean {
    return (
      this.hasBankAccount &&
      this.isSelectedAccountValid &&
      this.canOperate &&
      this.isGeolocationEnabled &&
      !this.isDiscretional
    );
  }

  public toggleBalanceVisibility() {
    this.isBalanceVisible = !this.isBalanceVisible;
  }

  public goToBankAccountRegistration() {
    this.onCloseWithdrawal.next(true);
    this.router.navigate([appRoutes.bankAccount]);
  }

  public onlyTextNumber(event: any, value: any) {
    if (
      (event.charCode >= 65 && event.charCode <= 90) ||
      (event.charCode >= 97 && event.charCode <= 122) ||
      (event.charCode >= 48 && event.charCode <= 57) ||
      event.charCode === 32
    ) {
      return (
        (event.charCode >= 65 && event.charCode <= 90) ||
        (event.charCode >= 97 && event.charCode <= 122) ||
        (event.charCode >= 48 && event.charCode <= 57) ||
        event.charCode === 32
      );
    } else {
      return event.charCode === 8 || event.charCode === 0
        ? undefined
        : (event.charCode >= 65 && event.charCode <= 90) ||
            (event.charCode >= 97 && event.charCode <= 122) ||
            (event.charCode >= 48 && event.charCode <= 57) ||
            event.charCode === 32;
    }
  }

  public assignValueConcept(event: any) {
    this.concepto = event.currentTarget.value;
  }

  public onlyNumberKey(event: any, value: any) {
    if (event.charCode >= 48 && event.charCode <= 57) {
      this.isWithDrawalReady(true);
      return event.charCode >= 48 && event.charCode <= 57;
    } else {
      return event.charCode === 8 || event.charCode === 0
        ? undefined
        : event.charCode >= 48 && event.charCode <= 57;
    }
  }

  public assignValueReference(event: any) {
    this.referencia = event.currentTarget.value;
  }

  public get isSelectedAccountValid() {
    return !this.selectedAccount
      ? true
      : this.selectedAccount && this.selectedAccount.isValid;
  }

  @HostListener('window:scroll', ['$event']) onWindowScroll($event: any) {
    this.doScrollArragenment();
  }

  private functiononly(event: any) {
    if (String.fromCharCode(event.keyCode) === undefined) {
      return '';
    }
    const cleanInputValue = String.fromCharCode(event.keyCode).replace(
      /[^\w\s]/gi,
      '',
    );
    if (cleanInputValue !== String.fromCharCode(event.keyCode)) {
      event.preventDefault();
    }
    return cleanInputValue;
  }

  private scanForUserBankAccounts() {
    this.subscriptions.add(
      this.store
        .select('userHasBankAccount')
        .pipe(
          filter((userHasBankAccount: any) => userHasBankAccount !== undefined),
        )
        .subscribe((userHasBankAccount: any) => {
          this.userHasBankAccountIssues = userHasBankAccount;
        }),
    );
  }

  private scanForUserPartyId() {
    this.subscriptions.add(
      this.store
        .select('partyId')
        .pipe(filter((partyId: any) => partyId !== undefined))
        .subscribe((partyId: any) => {
          this.userHasPartyId = partyId;
        }),
    );
  }

  private scanForIsServiceInterrupted() {
    this.subscriptions.add(
      this.store
        .select('serviceInterruption')
        .pipe(
          map((e: any) => (e && e.toJS()) || undefined),
          filter((state) => state !== undefined),
          pluck('position'),
        )
        .subscribe((isServiceInterrupted: boolean) => {
          this.isServiceInterrupted = isServiceInterrupted;
        }),
    );
  }

  private getBankAccountsEndpoint() {
    let bankAccountEndpoint$: Observable<BankAccountViewModel[]>;
    if (
      featureFlags.accountManagerHB.isEnabled() &&
      this.userCanUseAccountManager
    ) {
      const { legalPersonalityType } = this.currentContract;
      if (legalPersonalityType === LegalPersonalityType.natural) {
        bankAccountEndpoint$ = forkJoin(
          this.operationService.getWithdrawalAccounts(this.currentContractID),
          this.operationService.getBanksInformation(),
          (bankAccounts: BankAccountViewModel[], banks: any[]) => {
            const banksCollection = banks.reduce((accumulator, bank) => {
              accumulator[bank.referenceNumber] = bank;
              return accumulator;
            }, {});
            return bankAccounts.map((bankAccount) => {
              return {
                ...bankAccount,
                bankName: banksCollection[bankAccount.bankCode].shortName,
              };
            });
          },
        );
      } else {
        bankAccountEndpoint$ = this.cashService.getBankData(
          this.currentContractID,
        );
      }
    } else {
      bankAccountEndpoint$ = this.cashService.getBankData(
        this.currentContractID,
      );
    }
    return bankAccountEndpoint$;
  }

  private getBankList() {
    this.subscriptions.add(
      this.store
        .select('bankList')
        .pipe(
          filter((values) => !!values),
          map((values: any) => values.toJS()),
        )
        .subscribe((banks: any) => {
          this.bankList = banks;
        }),
    );
  }

  private getBankInfo(bankCode: number) {
    const bankInfo = {
      shortName: '',
      icon: '',
    };
    const foundBank =
      this.bankList &&
      this.bankList.find((bank: any) => {
        return bank.gbmBankId === bankCode;
      });

    if (foundBank) {
      bankInfo.shortName = foundBank.shortName;
      const foundIcon = this.bankWithIcon.find(
        (icon: string) => icon === foundBank.referenceNumber,
      );
      if (foundIcon) {
        bankInfo.icon = `/images/banks/atm-iso-${foundBank.referenceNumber}.svg`;
      }
    }
    return bankInfo;
  }

  private getAccountLabelAndName() {
    if (this.selectedLoremAccount) {
      if ((this.selectedLoremAccount as IContract).contractID) {
        this.originAccountName = (
          this.selectedLoremAccount as IContract
        ).contractID;
        this.originAccountLabel = 'Contrato origen';
      } else {
        this.originAccountName = (
          this.selectedLoremAccount as AccountViewModel
        ).name;
        this.originAccountLabel = 'Estrategia origen';
      }
    }
  }

  private showMissingBankAccount() {
    this.hasBankAccount = false;
    this.handleBankccountNotification();
    this.onCloseWithdrawal.next(true);
  }

  /**
   * Throws a notification with an anchor element to register a bank account
   *
   * NOTE: This can be error-prone, and will be difficult to debug/trace, as we
   * directly manipulate the DOM.
   *
   * Hopefully these comments will be helpful.
   *
   * In a nutshell, we create an element with a custom class that we search for
   * in the DOM after a 0 timeout, and append a listener to it.
   *
   * ---
   *
   * When a user tries to withdraw money, but has no registered bank account, we
   * throw a notification with that information.
   *
   * The simplest case, is Trading USA with no access to the dashboard, as we only
   * display a message.
   *
   * The two remaining cases require us to add an anchor element that either:
   *  a) Navigate to the bank-account section in the dashboard (Trading USA)
   *  b) Navigate to /main/cuenta-bancaria (Trading MX)
   *
   * Doing (a) it's easy, as we only need to add `href` and `target` attrs, and
   * it'll behave as you'd expect from an anchor tag.
   *
   * Doing (b) requires us to use the Angular Router to prevent us from reloading
   * the SPA from scratch. And it's the reason we needed to directly touch the DOM,
   * because **handlers can only be set after an element has been appended**.
   *
   * We considered some alternatives, like:
   *   - Adding the `onclick` handler when creating the anchor element.
   *   - Adding `onclick` as plain text and dispatch a custom event.
   *   - Catching clicks in the notification element.
   *
   * But all of these weren't feasible because of these reasons:
   *   - `onclick` being dispossed off when appending strings as HTML, as
   *      ngx-toastr does.
   *   - ngx-toastr stopping the propagation of any event inside the
   *      notification element.
   *   - ngx-toastr only receiving strings as messages (even when the enableHtml
   *      flag is true)
   */
  private handleBankccountNotification() {
    if (
      this.currentContract.legalPersonalityType ===
      LEGAL_PERSONALITY_TYPE.MORAL_PERSON
    ) {
      this.notificationService.showInfo({
        message: `Por favor, contacta al área Relación con el inversionista para registrar tu cuenta bancaria y poder retirar dinero cuando lo necesites`,
      });

      return;
    }

    // Create the anchor tag with our custom class
    const className = 'missing-bank-account-element';
    const anchor = this.document.createElement('a');
    anchor.classList.add(className);
    anchor.innerText = 'Cuenta bancaria';

    if (this.isUsaContract) {
      this.subscriptions.add(
        this.store
          .select('config')
          .pipe(
            filter((mapShadowed: any) => Map.isMap(mapShadowed)),
            map((mapShadowed: any) => mapShadowed.toJS()),
            first(),
          )
          .subscribe(({ comBankAccountUrl }) => {
            anchor.setAttribute('href', comBankAccountUrl);
            anchor.setAttribute('target', '_blank');
            anchor.setAttribute('rel', 'noopener noreferrer');
          }),
      );
    } else {
      // Using a 0-timeout so it runs after this method returns,
      // and we guarantee we'll find the appended HTML in the DOM.
      setTimeout(() => {
        // Query the anchor with our custom class
        const element = this.document.querySelector(
          `a.${className}`,
        ) as HTMLAnchorElement;

        // And append an onclick handler that uses the router
        element.onclick = () => {
          this.router.navigate([appRoutes.bankAccount]);
        };
      }, 0);
    }

    this.notificationService.showInfo({
      message: `Registra la CLABE y banco en la sección ${anchor.outerHTML} para poder retirar dinero cuando lo necesites`,
      options: { enableHtml: true },
    });
  }
}
